import { computed, toValue } from 'vue';
import {
  useQuery,
  type DefaultError,
  type QueryKey,
  type UndefinedInitialQueryOptions,
  type UseQueryReturnType,
} from '@tanstack/vue-query';
import type { MaybeId, QueryFlags } from './types';

/**
 * What a `queryOptions(...)` factory hands back. `UndefinedInitialQueryOptions`
 * is a `MaybeRef`; the `Exclude` picks its plain-object branch, which is the
 * only one that can be spread into the reactive options below.
 */
export type QueryOptionsFor<
  TData,
  TError = DefaultError,
  TQueryKey extends QueryKey = QueryKey,
> = Exclude<
  UndefinedInitialQueryOptions<TData, TError, TData, TQueryKey>,
  { value: unknown } | (() => unknown)
>;

export type ScopedQueryFlags<
  TData,
  TError = DefaultError,
  TQueryKey extends QueryKey = QueryKey,
> = QueryFlags &
  Omit<
    QueryOptionsFor<TData, TError, TQueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >;

/**
 * An observer over one `queryOptions` factory, gated on an id that may not be
 * there yet.
 *
 * `optionsFor` takes a plain string rather than a ref so that a changed id
 * rebuilds the key and the `queryFn` closure together: a fetcher for the
 * previous variant can never run against the next variant's key.
 */
export function useScopedQuery<
  TData,
  TError = DefaultError,
  TQueryKey extends QueryKey = QueryKey,
>(
  id: MaybeId,
  optionsFor: (id: string) => QueryOptionsFor<TData, TError, TQueryKey>,
  {
    enabled = true,
    ...overrides
  }: ScopedQueryFlags<TData, TError, TQueryKey> = {}
): UseQueryReturnType<TData, TError> {
  const resolved = computed(() => toValue(id) ?? '');
  const options = computed(() => ({
    ...optionsFor(resolved.value),
    ...overrides,
    enabled: Boolean(resolved.value) && toValue(enabled),
  }));

  return useQuery(options);
}
